import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { postService } from '../../../services/postService';
import { categoryService } from '../../../services/categoryService';
import ToastNotification from '../../components/common/ToastNotification';
import CategoryOrderModal from './CategoryOrderModal';

function PostForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [categoryOrders, setCategoryOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    coverImage: '',
    categoryId: '',
    isPublished: false,
    isTutorials: false,
    displayOrder: 0
  });

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      fetchPost();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAll();
      setCategories(response.data || []);
    } catch (err) {
      setError('Failed to fetch categories');
    }
  };

  const fetchPost = async () => {
    try {
      const response = await postService.getById(id);
      const post = response.data;
      setFormData({
        title: post.title || '',
        description: post.description || '',
        coverImage: post.coverImage || '',
        categoryId: post.categoryId || '',
        isPublished: post.isPublished || false,
        isTutorials: post.isTutorials || false,
        displayOrder: post.displayOrder || 0
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setInitialLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim() || !formData.categoryId) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      if (isEditMode) {
        await postService.update(id, formData);
        showNotification('Post updated successfully');
      } else {
        await postService.create(formData);
        showNotification('Post created successfully');
      }
      
      setTimeout(() => {
        navigate('/admin/posts');
      }, 1500);
    } catch (err) {
      setError(err.message);
      showNotification(err.message, 'danger');
      setLoading(false);
    }
  };

  const handleOpenOrderModal = async (categoryId) => {
    if (!categoryId) return;
    
    try {
      setLoadingOrders(true);
      setShowOrderModal(true);
      const res = await postService.getDisplayOrderByCategory(categoryId);
      setCategoryOrders(res.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingOrders(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="admin-crud-wrapper">
        <div className="admin-crud-loader">
          <div className="admin-crud-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-crud-wrapper">
      <div className="admin-crud-container">
        <div className="admin-crud-card">
          <div className="admin-crud-card-header">
            <h3 className="admin-crud-title">
              <span className="admin-crud-title-icon">{isEditMode ? '✏️' : '📝'}</span>
              {isEditMode ? 'Edit Post' : 'Create New Post'}
            </h3>
            <button 
              className="admin-crud-btn admin-crud-btn-secondary"
              onClick={() => navigate('/admin/posts')}
            >
              ← Back to Posts
            </button>
          </div>

          <div className="admin-crud-modal-body">
            {error && (
              <div className="admin-crud-toast" style={{ position: 'relative', marginBottom: '20px' }}>
                <div className="admin-crud-toast-content admin-crud-toast-danger">
                  <div className="admin-crud-toast-header">
                    <strong>Error</strong>
                    <button onClick={() => setError(null)}>✕</button>
                  </div>
                  <div className="admin-crud-toast-body">{error}</div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="admin-crud-form">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Left Column */}
                <div>
                  <div className="admin-crud-form-group">
                    <label className="admin-crud-label admin-crud-label-required">Title</label>
                    <input
                      type="text"
                      name="title"
                      className="admin-crud-input"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter post title"
                      required
                    />
                  </div>

                  <div className="admin-crud-form-group">
                    <label className="admin-crud-label admin-crud-label-required">Description</label>
                    <textarea
                      name="description"
                      className="admin-crud-textarea"
                      rows="12"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Write your post description..."
                      required
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <div className="admin-crud-form-group">
                    <label className="admin-crud-label admin-crud-label-required">Category</label>
                    <select
                      name="categoryId"
                      className="admin-crud-select"
                      value={formData.categoryId}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="admin-crud-form-group">
                    <label className="admin-crud-label">Display Order</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="number"
                        step="0.001"
                        name="displayOrder"
                        className="admin-crud-input"
                        value={formData.displayOrder}
                        onChange={handleChange}
                        placeholder="Enter display order"
                      />
                      <button
                        type="button"
                        className="admin-crud-btn admin-crud-btn-outline"
                        disabled={!formData.categoryId}
                        onClick={() => handleOpenOrderModal(formData.categoryId)}
                        style={{ padding: '8px 16px' }}
                      >
                        👁️ View Orders
                      </button>
                    </div>
                  </div>

                  <div className="admin-crud-form-group">
                    <label className="admin-crud-label">Cover Image URL</label>
                    <input
                      type="text"
                      name="coverImage"
                      className="admin-crud-input"
                      value={formData.coverImage}
                      onChange={handleChange}
                      placeholder="https://cdn.jsdelivr.net/gh/username/repo@main/image.jpg"
                    />
                    {formData.coverImage && (
                      <div style={{ marginTop: '12px' }}>
                        <img
                          src={formData.coverImage}
                          alt="Preview"
                          style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML += '<div class="admin-crud-error-message">❌ Invalid image URL</div>';
                          }}
                        />
                      </div>
                    )}
                    <small className="admin-crud-empty-subtext">
                      Use GitHub CDN format for better performance
                    </small>
                  </div>

                  <div className="admin-crud-form-group">
                    <div className="admin-crud-switch">
                      <span className="admin-crud-switch-label">Post Type</span>
                      <label className="admin-crud-switch">
                        <input
                          type="checkbox"
                          name="isTutorials"
                          checked={formData.isTutorials}
                          onChange={handleChange}
                        />
                      </label>
                    </div>
                    <small className="admin-crud-empty-subtext">
                      Enable for Tutorial, disable for Blog
                    </small>
                  </div>

                  <div className="admin-crud-form-group">
                    <div className="admin-crud-switch">
                      <span className="admin-crud-switch-label">Status</span>
                      <label className="admin-crud-switch">
                        <input
                          type="checkbox"
                          name="isPublished"
                          checked={formData.isPublished}
                          onChange={handleChange}
                        />
                      </label>
                    </div>
                    <small className="admin-crud-empty-subtext">
                      Enable to publish immediately
                    </small>
                  </div>
                </div>
              </div>

              <div className="admin-crud-modal-footer" style={{ padding: '20px 0 0 0' }}>
                <button
                  type="button"
                  className="admin-crud-btn admin-crud-btn-secondary"
                  onClick={() => navigate('/admin/posts')}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-crud-btn admin-crud-btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEditMode ? 'Update Post' : 'Create Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Category Order Modal */}
      <CategoryOrderModal
        show={showOrderModal}
        onHide={() => setShowOrderModal(false)}
        loading={loadingOrders}
        data={categoryOrders}
      />

      {/* Toast Notification */}
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type={toastType}
      />
    </div>
  );
}

export default PostForm;